/**
 * Build lifecycle helpers — custom + speculative wig builds.
 *
 * The Phase 1+2+3 inventory loop adds a `lifecycleStatus` column to
 * WigAsset and ties WorkTasks to BuildStages so the customer can see
 * where their wig is in the build pipeline. This file exposes the
 * cross-cutting helpers used by the checkout, admin builds page,
 * 2-week heartbeat cron, balance collection, and customer progress
 * page.
 *
 * Sister files:
 * - lib/wig-assets.ts        — barcode minting + ready-stock auto-claim
 * - lib/balance-collection.ts — Square charge-balance flow (3 modes)
 */

import { prisma } from "@/lib/prisma";
import { createWigAsset } from "@/lib/wig-assets";
import { getSiteConfigFresh } from "@/lib/site-config";
import type { CustomWigOrder, OrderItem, Prisma } from "@prisma/client";

/**
 * Compute the ETA range (in absolute Date instants) for a build of a
 * given product. Resolution order:
 *   1. Per-Product override (Product.buildDurationOverrideWeeksMin/Max)
 *   2. Per-category default (SiteConfig.buildDurationByCategory[slug])
 *   3. Hardcoded fallback (4–6 weeks) for legacy products with no
 *      category match.
 * Returns the *midpoint* as the ETA (Rivky can override per asset on
 * the build page); the customer page renders ±2 days around it.
 */
export async function computeETA(input: {
  productId?: string | null;
  categorySlug?: string | null;
}): Promise<{ midpoint: Date; minWeeks: number; maxWeeks: number }> {
  const cfg = await getSiteConfigFresh();
  const byCategory = cfg.buildDurationByCategory ?? {};

  let minWeeks = 4;
  let maxWeeks = 6;

  if (input.productId) {
    const product = await prisma.product.findUnique({
      where: { id: input.productId },
      select: {
        buildDurationOverrideWeeksMin: true,
        buildDurationOverrideWeeksMax: true,
        category: { select: { slug: true } },
      },
    });
    if (product) {
      if (product.buildDurationOverrideWeeksMin && product.buildDurationOverrideWeeksMax) {
        minWeeks = product.buildDurationOverrideWeeksMin;
        maxWeeks = product.buildDurationOverrideWeeksMax;
      } else if (product.category?.slug && byCategory[product.category.slug]) {
        const range = byCategory[product.category.slug];
        minWeeks = range.minWeeks;
        maxWeeks = range.maxWeeks;
      }
    }
  } else if (input.categorySlug && byCategory[input.categorySlug]) {
    const range = byCategory[input.categorySlug];
    minWeeks = range.minWeeks;
    maxWeeks = range.maxWeeks;
  }

  const avgDays = ((minWeeks + maxWeeks) / 2) * 7;
  const midpoint = new Date(Date.now() + avgDays * 86400_000);
  return { midpoint, minWeeks, maxWeeks };
}

/**
 * Recompute WigAsset.currentStageId + estimatedReadyAt based on its
 * WorkTasks. Currentstage = earliest open stage (by sortOrder).
 * ETA = today + sum(BuildStage.expectedDays) for stages whose tasks
 * are still open.
 *
 * If every task is done, sets currentStageId = null and bumps
 * lifecycleStatus to "quality_check" (so admin sees "ready for QA").
 *
 * Called from WorkTask PATCH handlers after every status change. The
 * ETA recomputes naturally as stages close, keeping the customer's
 * progress page honest as builds slip or accelerate.
 *
 * Honors a per-asset hand-typed override: if admin manually sets
 * estimatedReadyAt on an asset that hadn't yet had any task close,
 * we leave it alone. Once tasks start closing (auto-ETA becomes more
 * reliable than the upfront guess), auto-ETA takes over.
 */
export async function recomputeCurrentStage(wigAssetId: string): Promise<void> {
  const tasks = await prisma.workTask.findMany({
    where: { wigAssetId, stageId: { not: null } },
    include: {
      stage: { select: { id: true, sortOrder: true, expectedDays: true } },
    },
    orderBy: { createdAt: "asc" },
  });

  if (tasks.length === 0) return; // no build tasks; legacy service-only flow

  // Find open tasks (assigned / in_progress / ready_for_approval).
  const openTasks = tasks.filter(
    (t) => t.status === "assigned" || t.status === "in_progress" || t.status === "ready_for_approval"
  );

  if (openTasks.length === 0) {
    // All build tasks closed; flip to QA review state for admin.
    await prisma.wigAsset.update({
      where: { id: wigAssetId },
      data: {
        currentStageId: null,
        lifecycleStatus: "quality_check",
      },
    });
    return;
  }

  // Earliest open stage = current.
  const earliestStage = openTasks
    .filter((t) => t.stage)
    .map((t) => t.stage!)
    .sort((a, b) => a.sortOrder - b.sortOrder)[0];

  // ETA = today + sum(expectedDays for unique stages that still have
  // open tasks). Dedup so a stage with 3 open tasks still contributes
  // expectedDays once. (Per-stage parallelism is the typical case —
  // ventilation may have several tasks but they happen concurrently.)
  const stageIdsWithOpen = new Set<string>();
  let remainingDays = 0;
  for (const t of openTasks) {
    if (!t.stage) continue;
    if (stageIdsWithOpen.has(t.stage.id)) continue;
    stageIdsWithOpen.add(t.stage.id);
    remainingDays += t.stage.expectedDays;
  }
  const newEta = remainingDays > 0
    ? new Date(Date.now() + remainingDays * 86400_000)
    : null;

  await prisma.wigAsset.update({
    where: { id: wigAssetId },
    data: {
      currentStageId: earliestStage?.id ?? null,
      ...(newEta ? { estimatedReadyAt: newEta } : {}),
    },
  });
}

/**
 * Render a per-stage SMS / email template against a WigAsset. Replaces:
 *   {{customerName}} {{barcodeId}} {{stageLabel}} {{stageDescription}}
 *   {{etaRange}} {{progressLink}}
 */
export function renderStageTemplate(
  template: string,
  ctx: {
    customerName: string;
    barcodeId: string;
    stageLabel: string;
    stageDescription: string;
    etaRange: string;
    progressLink: string;
  }
): string {
  return template
    .replaceAll("{{customerName}}", ctx.customerName)
    .replaceAll("{{barcodeId}}", ctx.barcodeId)
    .replaceAll("{{stageLabel}}", ctx.stageLabel)
    .replaceAll("{{stageDescription}}", ctx.stageDescription)
    .replaceAll("{{etaRange}}", ctx.etaRange)
    .replaceAll("{{progressLink}}", ctx.progressLink);
}

/**
 * Format an ETA Date as a ±2 day range like "May 18–22". Uses studio
 * timezone for display so a UTC instant near midnight doesn't bounce
 * to the wrong day.
 */
export function formatEtaRange(eta: Date | null | undefined): string {
  if (!eta) return "TBD";
  const min = new Date(eta.getTime() - 2 * 86400_000);
  const max = new Date(eta.getTime() + 2 * 86400_000);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "America/New_York",
    });
  return `${fmt(min)}–${fmt(max).replace(/^\w+ /, "")}`;
}

/**
 * Mint a WigAsset for a freshly-paid custom wig order item. Auto-creates
 * a hidden Product placeholder so the asset has a parent (used for
 * reporting + the customer progress page's product fallback). Auto-
 * scaffolds WorkTasks from the matching BuildTemplate.
 *
 * Idempotent on re-call: if the OrderItem already has a WigAsset, returns
 * the existing one (skip path for Square webhook re-deliveries).
 *
 * Returns the created WigAsset id + barcode for downstream notifications.
 */
export async function mintCustomWigAsset(args: {
  orderItem: Pick<OrderItem, "id" | "productName" | "productImage" | "productId">;
  customWigOrder: Pick<CustomWigOrder, "id" | "baseType" | "texture" | "length" | "color">;
  customerUserId: string;
  adminUserId: string;
}): Promise<{ wigAssetId: string; barcodeId: string; alreadyExists: boolean }> {
  // Idempotent guard
  const existing = await prisma.wigAsset.findUnique({
    where: { orderItemId: args.orderItem.id },
    select: { id: true, barcodeId: true },
  });
  if (existing) {
    return { wigAssetId: existing.id, barcodeId: existing.barcodeId, alreadyExists: true };
  }

  // Use the OrderItem.productId if present (rare for fully-custom);
  // otherwise auto-create a hidden Product placeholder.
  let productId = args.orderItem.productId ?? null;
  let categorySlug: string | null = null;

  if (productId) {
    const prod = await prisma.product.findUnique({
      where: { id: productId },
      select: { category: { select: { slug: true } } },
    });
    categorySlug = prod?.category?.slug ?? null;
  } else {
    // Find or create a "Custom Builds" category, then create hidden product.
    const customCategory = await prisma.category.upsert({
      where: { slug: "custom-builds" },
      update: {},
      create: {
        name: "Custom Builds",
        slug: "custom-builds",
        description: "Auto-created bucket for fully-bespoke custom-builder orders.",
      },
    });
    const slug = `custom-${args.customWigOrder.id.toLowerCase()}`;
    const placeholder = await prisma.product.create({
      data: {
        name: args.orderItem.productName,
        slug,
        categoryId: customCategory.id,
        price: 0, // price lives on the OrderItem; this row is just a placeholder
        isActive: true,
        isHidden: true,
        stockCount: 0,
        inStock: false,
        baseType: args.customWigOrder.baseType,
        texture: args.customWigOrder.texture,
        length: args.customWigOrder.length,
      },
    });
    productId = placeholder.id;
    categorySlug = "custom-builds";
  }

  // Pick a build template that matches the category (custom-builds falls
  // back to the "Custom Build" template — categorySlug=null in seed).
  const template = await pickBuildTemplate(categorySlug);
  const eta = await computeETA({ productId, categorySlug });

  const asset = await createWigAsset({
    ownerId: args.customerUserId,
    orderItemId: args.orderItem.id,
    productId,
    name: args.orderItem.productName,
    description: `Custom: ${args.customWigOrder.baseType} · ${args.customWigOrder.texture} ${args.customWigOrder.length} · ${args.customWigOrder.color}`,
    photoUrl: args.orderItem.productImage,
  });

  await prisma.wigAsset.update({
    where: { id: asset.id },
    data: {
      lifecycleStatus: "in_production",
      buildStartedAt: new Date(),
      estimatedReadyAt: eta.midpoint,
      buildTemplateId: template?.id ?? null,
      listedForSale: false, // claimed builds are never listable
    },
  });

  if (template) {
    await scaffoldWorkTasksFromTemplate({
      wigAssetId: asset.id,
      templateId: template.id,
      assignedById: args.adminUserId,
    });
    // After tasks exist, currentStageId = first stage
    await recomputeCurrentStage(asset.id);
  }

  return { wigAssetId: asset.id, barcodeId: asset.barcodeId, alreadyExists: false };
}

/**
 * Pick the best BuildTemplate for a given category. Prefers an active
 * template whose categorySlug matches; falls back to the "all categories"
 * template (categorySlug = null), then any active template.
 */
export async function pickBuildTemplate(categorySlug: string | null) {
  if (categorySlug) {
    const exact = await prisma.buildTemplate.findFirst({
      where: { isActive: true, categorySlug },
      orderBy: { sortOrder: "asc" },
    });
    if (exact) return exact;
  }
  const universal = await prisma.buildTemplate.findFirst({
    where: { isActive: true, categorySlug: null },
    orderBy: { sortOrder: "asc" },
  });
  if (universal) return universal;
  return prisma.buildTemplate.findFirst({
    where: { isActive: true },
    orderBy: { sortOrder: "asc" },
  });
}

/**
 * Create one WorkTask per BuildTemplateStage for a fresh build. Each
 * task starts in `assigned` status with default instructions copied
 * from the template. Admin can edit/reassign individual tasks later.
 */
export async function scaffoldWorkTasksFromTemplate(args: {
  wigAssetId: string;
  templateId: string;
  assignedById: string;
  assignedToId?: string;
}): Promise<{ created: number }> {
  const stages = await prisma.buildTemplateStage.findMany({
    where: { templateId: args.templateId },
    include: { stage: true },
    orderBy: { sortOrder: "asc" },
  });

  // Generate task numbers in a single pass — find current max once.
  const last = await prisma.workTask.findFirst({
    orderBy: { taskNumber: "desc" },
    select: { taskNumber: true },
  });
  const lastNum = last?.taskNumber.match(/WT-(\d+)/)?.[1];
  let nextNum = (lastNum ? parseInt(lastNum, 10) : 0) + 1;

  const data: Prisma.WorkTaskCreateManyInput[] = stages.map((ts) => ({
    taskNumber: `WT-${String(nextNum++).padStart(4, "0")}`,
    wigAssetId: args.wigAssetId,
    stageId: ts.stageId,
    assignedById: args.assignedById,
    assignedToId: args.assignedToId ?? null,
    taskType: ts.stage.internalLabel,
    instructions: ts.defaultTaskInstructions,
    status: "assigned",
    priority: "normal",
  }));

  const result = await prisma.workTask.createMany({ data });
  return { created: result.count };
}
